<template>
  <div class="card">
    <div class="card-body">
      <h5 class="card-title mb-4">Manager Review</h5>

      <div v-if="!canReview" class="alert alert-info">
        <i class="icon-feather-info me-2"></i>
        Manager review is not available at this stage. Current status: {{ formatStatus(review.status) }}
      </div>

      <div v-else-if="isCompleted" class="alert alert-success">
        <i class="icon-feather-check-circle me-2"></i>
        Manager review has been completed.
      </div>

      <form v-else @submit.prevent="submitReview">
        <!-- Summary -->
        <div class="mb-4">
          <h6 class="text-primary mb-3">Summary <span class="text-danger">*</span></h6>
          <textarea
            v-model="formData.summary"
            class="form-control"
            rows="5"
            placeholder="Provide an overall summary of the employee's performance, including key achievements and recommendations..."
            required
          ></textarea>
        </div>

        <!-- Strengths -->
        <div class="mb-4">
          <h6 class="text-primary mb-3">Strengths</h6>
          <textarea
            v-model="formData.strengths"
            class="form-control"
            rows="3"
            placeholder="Key strengths observed during the review period (optional)"
          ></textarea>
        </div>

        <!-- Areas for Improvement -->
        <div class="mb-4">
          <h6 class="text-primary mb-3">Areas for Improvement</h6>
          <textarea
            v-model="formData.areas_for_improvement"
            class="form-control"
            rows="3"
            placeholder="Areas where the employee can improve (optional)"
          ></textarea>
        </div>

        <!-- Development Plan -->
        <div class="mb-4">
          <h6 class="text-primary mb-3">Development Plan</h6>
          <textarea
            v-model="formData.development_plan"
            class="form-control"
            rows="3"
            placeholder="Suggested development plan (optional)"
          ></textarea>
        </div>

        <!-- Manager Comments -->
        <div class="mb-4">
          <h6 class="text-primary mb-3">Manager Comments</h6>
          <textarea
            v-model="formData.manager_comments"
            class="form-control"
            rows="3"
            placeholder="Any additional manager comments (optional)"
          ></textarea>
        </div>

        <!-- Overall Score and Rating -->
        <div class="row mb-4">
          <div class="col-md-6">
            <label class="form-label">Overall Score (0-100) <small class="text-muted">(optional)</small></label>
            <input
              type="number"
              class="form-control"
              step="0.1"
              min="0"
              max="100"
              v-model.number="formData.overall_score"
              placeholder="e.g. 85"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label">Rating <small class="text-muted">(optional)</small></label>
            <select v-model="formData.rating" class="form-select">
              <option value="">Select rating</option>
              <option value="exceptional">Exceptional</option>
              <option value="exceeds">Exceeds Expectations</option>
              <option value="meets">Meets Expectations</option>
              <option value="needs_improvement">Needs Improvement</option>
              <option value="unsatisfactory">Unsatisfactory</option>
            </select>
          </div>
        </div>

        <!-- Submit Buttons -->
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-secondary" @click="saveDraft">
            <i class="icon-feather-save me-2"></i>Save Draft
          </button>
          <button type="submit" class="btn btn-primary" :disabled="submitting || !canSubmit">
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="icon-feather-send me-2"></i>
            Submit Manager Review
          </button>
        </div>
      </form>

      <!-- Display completed review -->
      <div v-if="isCompleted">
        <div class="mb-3">
          <h6 class="text-primary">Summary</h6>
          <p>{{ review.summary }}</p>
        </div>

        <div v-if="review.strengths" class="mb-3">
          <h6 class="text-primary">Strengths</h6>
          <p>{{ review.strengths }}</p>
        </div>

        <div v-if="review.areas_for_improvement" class="mb-3">
          <h6 class="text-primary">Areas for Improvement</h6>
          <p>{{ review.areas_for_improvement }}</p>
        </div>

        <div v-if="review.development_plan" class="mb-3">
          <h6 class="text-primary">Development Plan</h6>
          <p>{{ review.development_plan }}</p>
        </div>

        <div v-if="review.manager_comments" class="mb-3">
          <h6 class="text-primary">Manager Comments</h6>
          <p>{{ review.manager_comments }}</p>
        </div>

        <div class="row">
          <div class="col-md-6" v-if="review.overall_score !== null">
            <h6 class="text-primary">Overall Score</h6>
            <div class="progress mb-2" style="height: 30px;">
              <div
                class="progress-bar"
                :class="getScoreBarClass(review.overall_score)"
                :style="`width: ${review.overall_score}%`"
              >
                {{ review.overall_score }}%
              </div>
            </div>
          </div>
          <div class="col-md-6" v-if="review.rating">
            <h6 class="text-primary">Rating</h6>
            <span :class="getRatingBadgeClass(review.rating)">
              {{ formatRating(review.rating) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import notify from '@/utils/notify';

export default {
  name: 'ManagerReviewForm',
  props: {
    review: {
      type: Object,
      required: true
    },
    canSubmit: {
      type: Boolean,
      default: false
    }
  },
  emits: ['submitted'],
  setup(props, { emit }) {
    const submitting = ref(false);
    const formData = ref({
      summary: '',
      strengths: '',
      areas_for_improvement: '',
      development_plan: '',
      manager_comments: '',
      overall_score: null,
      rating: ''
    });

    const canReview = computed(() =>
      props.review.status === 'manager_review' ||
      props.review.status === 'calibration'
    );

    const isCompleted = computed(() =>
      props.review.status === 'completed'
    );

    const saveDraft = () => {
      const draftData = { reviewId: props.review.id, ...formData.value };
      localStorage.setItem(`manager_review_draft_${props.review.id}`, JSON.stringify(draftData));
      notify.alert.success('Draft saved successfully');
    };

    const loadDraft = () => {
      const draftData = localStorage.getItem(`manager_review_draft_${props.review.id}`);
      if (draftData) {
        const parsed = JSON.parse(draftData);
        formData.value.summary = parsed.summary || formData.value.summary;
        formData.value.strengths = parsed.strengths || formData.value.strengths;
        formData.value.areas_for_improvement = parsed.areas_for_improvement || formData.value.areas_for_improvement;
        formData.value.development_plan = parsed.development_plan || formData.value.development_plan;
        formData.value.manager_comments = parsed.manager_comments || formData.value.manager_comments;
        formData.value.overall_score = parsed.overall_score ?? formData.value.overall_score;
        formData.value.rating = parsed.rating || formData.value.rating;
        notify.alert.info('Draft loaded successfully');
      }
    };

    const submitReview = async () => {
      if (!props.canSubmit) {
        notify.alert.error('You are not authorized to submit this review');
        return;
      }

      if (!formData.value.summary) {
        notify.alert.error('Please provide an overall performance summary');
        return;
      }

      submitting.value = true;

      try {
        const payload = {
          summary: formData.value.summary,
          strengths: formData.value.strengths || undefined,
          areas_for_improvement: formData.value.areas_for_improvement || undefined,
          development_plan: formData.value.development_plan || undefined,
          manager_comments: formData.value.manager_comments || undefined,
          overall_score: (formData.value.overall_score === null || formData.value.overall_score === '' ? undefined : formData.value.overall_score),
          rating: formData.value.rating || undefined
        };

        emit('submitted', payload);

        // Clear draft after successful submission
        localStorage.removeItem(`manager_review_draft_${props.review.id}`);
      } catch (error) {
        console.error('Failed to submit manager review:', error);
      } finally {
        submitting.value = false;
      }
    };

    const formatStatus = (status) => {
      const statusMap = {
        pending: 'Pending',
        self_review: 'Self Review',
        manager_review: 'Manager Review',
        calibration: 'Calibration',
        completed: 'Completed'
      };
      return statusMap[status] || status;
    };

    const formatRating = (rating) => {
      const ratingMap = {
        exceptional: 'Exceptional',
        exceeds: 'Exceeds Expectations',
        meets: 'Meets Expectations',
        needs_improvement: 'Needs Improvement',
        unsatisfactory: 'Unsatisfactory'
      };
      return ratingMap[rating] || rating;
    };

    const getRatingBadgeClass = (rating) => {
      const classes = {
        exceptional: 'badge bg-success fs-5',
        exceeds: 'badge bg-primary fs-5',
        meets: 'badge bg-info fs-5',
        needs_improvement: 'badge bg-warning fs-5',
        unsatisfactory: 'badge bg-danger fs-5'
      };
      return classes[rating] || 'badge bg-secondary fs-5';
    };

    const getScoreBarClass = (score) => {
      if (score >= 90) return 'bg-success';
      if (score >= 75) return 'bg-primary';
      if (score >= 60) return 'bg-info';
      if (score >= 40) return 'bg-warning';
      return 'bg-danger';
    };

    onMounted(() => {
      // initialize form from review for editing prefill
      formData.value.summary = props.review.summary || '';
      formData.value.strengths = props.review.strengths || '';
      formData.value.areas_for_improvement = props.review.areas_for_improvement || '';
      formData.value.development_plan = props.review.development_plan || '';
      formData.value.manager_comments = props.review.manager_comments || '';
      formData.value.overall_score = props.review.overall_score ?? null;
      formData.value.rating = props.review.rating || '';
      if (canReview.value) loadDraft();
    });

    return {
      submitting,
      formData,
      canReview,
      isCompleted,
      saveDraft,
      submitReview,
      formatStatus,
      formatRating,
      getRatingBadgeClass,
      getScoreBarClass
    };
  }
};
</script>

<style scoped>
.form-range {
  flex: 1;
}
</style>
